import { useEffect, useRef, useState } from 'react';
import type { GonfalonWindow } from '@gonfalon/types/GonfalonWindow';

// Extend the Window interface to include MktoForms2
declare global {
  interface Window extends GonfalonWindow {}
}

interface MarketoFormOptions {
  /**
   * The Marketo form ID to load
   */
  formId: string;
  /**
   * Optional callback that runs when the form is successfully submitted
   */
  onSuccess?: () => void;
  /**
   * Optional callback that runs when the form is submitted, before submission is processed
   */
  onSubmit?: () => void;
}

/**
 * Hook to handle Marketo form integration
 *
 * @param options - Configuration options for the Marketo form
 * @param options.formId - The Marketo form ID to load
 * @param options.onSuccess - Optional callback that runs when the form is successfully submitted
 * @param options.onSubmit - Optional callback that runs when the form is submitted, before submission is processed
 *
 * @returns An object containing:
 * - scriptLoaded: boolean indicating if the Marketo script has loaded
 * - formReady: boolean indicating if the Marketo form instance is ready to submit
 * - updateFormValues: function to update form field values programmatically
 * - onSubmit: function to programmatically submit the form (returns whether submission was started)
 *
 * There are 2 ways to pass form values to the marketo form object: using updateFormValues or directly
 * passing form values to onSubmit.
 *
 * Note: The form field names (Email, Company, ..., Description__c) are defined in marketo and we must conform to them.
 * If there is an existing marketo form you can view-source and get the field names from the source directly.
 * Alternatively, get the field names from the Marketo dashboard directly.
 *
 * @example
 * ```tsx
 * const { scriptLoaded, updateFormValues, onSubmit } = useMarketoForm({
 *   formId: '3144',
 *   onSuccess: () => console.log('Form submitted successfully'),
 *   onSubmit: () => console.log('Form is being submitted')
 * });
 *
 * // 1. Update form values when inputs change:
 * useEffect(() => {
 *   if (email) {
 *     updateFormValues({ Email: email });
 *   }
 * }, [email, updateFormValues]);
 *
 * // Submit the form programmatically
 * const handleSubmit = () => {
 *   onSubmit();
 * };
 *
 * // 2. You can also pass form values onSubmit:
 * onSubmit({
 *   Email: email,
 *   Company: company,
 *   FirstName: firstName,
 *   LastName: lastName,
 *   // eslint-disable-next-line @typescript-eslint/naming-convention
 *   Description__c: moreDetails,
 * });
 * ```
 */

const baseUrl = '//go.launchdarkly.com';
const munchkinId = '850-KKH-319';

export function useMarketoForm({ formId, onSuccess, onSubmit }: MarketoFormOptions) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [form, setForm] = useState<HTMLFormElement | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onSubmitRef = useRef(onSubmit);

  onSuccessRef.current = onSuccess;
  onSubmitRef.current = onSubmit;

  const attachFormHandlers = (marketoForm: HTMLFormElement) => {
    marketoForm.onSuccess(() => {
      onSuccessRef.current?.();
      // this prevents the page from reloading when the form is submitted
      return false;
    });
    setForm(marketoForm);
  };

  // Load the Marketo form script
  useEffect(() => {
    if (scriptLoaded && window.MktoForms2) {
      const existingForm = window.MktoForms2.getForm(formId);
      if (existingForm) {
        attachFormHandlers(existingForm);
        return;
      }

      window.MktoForms2.loadForm(baseUrl, munchkinId, formId, attachFormHandlers);
    }

    if (window.MktoForms2) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = '//go.launchdarkly.com/js/forms2/js/forms2.min.js';
    script.addEventListener('load', () => {
      if (window.MktoForms2) {
        setScriptLoaded(true);
      }
    });
    document.body.appendChild(script);
  }, [scriptLoaded, formId]);

  // Update form values
  const updateFormValues = (values: Record<string, unknown>) => {
    if (scriptLoaded && form) {
      form.setValues(values);
    }
  };

  // Submit the form with values if provided.
  const onFormSubmit = (values?: Record<string, unknown>): boolean => {
    if (scriptLoaded && form) {
      if (values) {
        updateFormValues(values);
      }

      form.submit();
      return true;
    }

    return false;
  };

  return {
    scriptLoaded,
    formReady: Boolean(form),
    updateFormValues,
    onSubmit: onFormSubmit,
  };
}
